import * as React from "react";
import { listAllocationDimensionRequest } from "actions/allocation-dimension";
import { bulkMutateAllocationDimensionValues } from "actions/collections";
import { fetchSchema, getMerchantPreferenceRequest } from "actions/merchant";
import { SchemaTypes } from "constants/merchant";
import {
    AllocationDimensionOwnerObject,
    IAllocationDimension,
    IAllocationDimensionValue,
} from "helpers/allocation-dimension";
import { connect } from "react-redux";
import { IStore } from "reducers";
import { Dimension } from "reducers/dimensions-panel/dimensions-panel";
import { getMerchantDefaultGenericAllocationDimensionsWithActiveValues } from "reducers/selectors/merchant";
import { parseFormStateIntoMutationPayload, useEditDialogInitialState } from "../../utils";
import { selectedDimensionValues } from "components";
import { EditAssortmentDialog } from "./edit-assortment-dialog";
import { ConfirmDialog, IconButton, Loading } from "@nuorder/components";
import { formatMessage } from "lib/globalize-util";
import { DATATEST } from "constants/integration";

type StateProps = {
    merchantId: string;
    merchantDimensions: IAllocationDimension[];
    driveItem?: nuorder.folder.v1.Item;
    allocationDimensionsOptions: Dimension[];
};

type ActionsProps = {
    onFetchMerchantAllocationDimensions: (merchantId: string) => void;
    onFetchMerchantPreference: (merchantId: string) => void;
    onFetchMerchantSchema: (merchantId: string) => void;
    bulkMutateAllocationDimensionValues: (allocationDimensionId: string, items: IAllocationDimensionValue[]) => void;
};

export const EditAssortmentDialogIcon = ({
    merchantId,
    merchantDimensions,
    driveItem,
    onFetchMerchantAllocationDimensions,
    onFetchMerchantPreference,
    onFetchMerchantSchema,
    bulkMutateAllocationDimensionValues,
    allocationDimensionsOptions,
}: ActionsProps & StateProps) => {
    const [isDialogOpen, setIsDialogOpen] = React.useState<boolean>(false);

    React.useEffect(() => {
        // We don't need this if component is not visible
        if (isDialogOpen) {
            onFetchMerchantPreference(merchantId);
            onFetchMerchantAllocationDimensions(merchantId);
            onFetchMerchantSchema(merchantId);
        }
    }, [
        merchantId,
        isDialogOpen,
        onFetchMerchantPreference,
        onFetchMerchantAllocationDimensions,
        onFetchMerchantSchema,
    ]);

    const isFetchingMerchantDimensions = !merchantDimensions.length;

    const { formValues, dimensionsDropdownOptions } = useEditDialogInitialState(
        isFetchingMerchantDimensions,
        merchantDimensions,
        allocationDimensionsOptions,
        driveItem
    );

    const onConfirm = React.useCallback(
        (formState: selectedDimensionValues) => {
            const payload = parseFormStateIntoMutationPayload(merchantDimensions, dimensionsDropdownOptions)(formState);
            Object.keys(payload).forEach((allocatedDimensionId) => {
                if (payload[allocatedDimensionId].length > 0) {
                    bulkMutateAllocationDimensionValues(allocatedDimensionId, payload[allocatedDimensionId]);
                }
            });
            onCloseDialog();
        },
        [dimensionsDropdownOptions, merchantDimensions, bulkMutateAllocationDimensionValues]
    );

    const onCloseDialog = () => setIsDialogOpen(false);

    return (
        <>
            <IconButton
                role="button"
                className="item"
                aria-haspopup
                title={formatMessage("Add grouping")}
                onClick={() => {
                    setIsDialogOpen(true);
                }}
                menuIconProps={{
                    iconName: "pencil24",
                }}
                data-test={DATATEST.ASSORTMENTS.GROUPING.ATTRIBUTES.OPEN_ADD_ATTRIBUTE_ITEM}
            />
            {isDialogOpen && isFetchingMerchantDimensions ? (
                <LoadingDialog onClose={onCloseDialog} />
            ) : isDialogOpen ? (
                <EditAssortmentDialog
                    onClose={onCloseDialog}
                    formValues={formValues}
                    dimensionsDropdownOptions={dimensionsDropdownOptions}
                    onConfirm={onConfirm}
                />
            ) : null}
        </>
    );
};

type LoadingDialogProps = {
    onClose: () => void;
};

const LoadingDialog = ({ onClose }: LoadingDialogProps) => {
    return (
        <ConfirmDialog
            contentProps={{ title: formatMessage("Update Assortment") }}
            onClose={onClose}
            onConfirm={onClose}
            open={true}
            okLabel={formatMessage("Save")}
            cancelLabel={formatMessage("Cancel")}
            closeButtonAriaLabel={formatMessage("Close")}
        >
            <Loading />
        </ConfirmDialog>
    );
};

const mapStateToProps = (state: IStore) => ({
    merchantId: state.collection.merchantId as string, // TODO: TECH DEBT collection type is not in IStore
    driveItem: state.collection.driveItem,
    merchantDimensions: getMerchantDefaultGenericAllocationDimensionsWithActiveValues(state),
    allocationDimensionsOptions: state.dimensionsPanel.draft.dimensions,
});

const mapDispatchToProps = (dispatch) => {
    return {
        onFetchMerchantAllocationDimensions: (merchantId: string) =>
            dispatch(listAllocationDimensionRequest(merchantId, AllocationDimensionOwnerObject.Merchant)),
        onFetchMerchantPreference: (merchantId: string) => dispatch(getMerchantPreferenceRequest(merchantId)),
        onFetchMerchantSchema: (merchantId: string) => dispatch(fetchSchema(merchantId, SchemaTypes.Items)),
        bulkMutateAllocationDimensionValues: (allocationDimensionId: string, items: IAllocationDimensionValue[]) => {
            dispatch(bulkMutateAllocationDimensionValues(allocationDimensionId, items));
        },
    };
};

export const EditAssortmentDialogIconContainer = connect(mapStateToProps, mapDispatchToProps)(EditAssortmentDialogIcon);
